import { Extension } from "@codemirror/state"
import { StateEffect, StateField } from "@codemirror/state"
import { Decoration, DecorationSet, EditorView } from "@codemirror/view"
import { findMatchingBracket } from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/bracketMatching/findMatchingBracket"
import { MATCHING_BRACKET_CLASS } from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/expressionTheme"

// Define a state effect to update hover position
const setHoverPos = StateEffect.define<number | null>()

/**
 * StateField that tracks hover position and creates decorations
 * for matching brackets when hovering over a bracket character
 */
const bracketHoverField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none
  },

  update(decorations, transaction) {
    // Look for hover position effects
    let hoverPos: number | null = null

    for (const effect of transaction.effects) {
      if (effect.is(setHoverPos)) {
        hoverPos = effect.value
      }
    }

    // If no hover effect, keep existing decorations
    if (hoverPos === null && transaction.effects.length === 0) {
      return decorations
    }

    // Clear decorations if hover is null
    if (hoverPos === null) {
      return Decoration.none
    }

    const newDecorations: { from: number; to: number }[] = []

    // Check character at hover position
    const char = transaction.state.doc.sliceString(hoverPos, hoverPos + 1)
    let match = findMatchingBracket(transaction.state, hoverPos, char)

    // If no match, try character before hover position (for closing brackets)
    if (!match && hoverPos > 0) {
      const charBefore = transaction.state.doc.sliceString(hoverPos - 1, hoverPos)
      match = findMatchingBracket(transaction.state, hoverPos - 1, charBefore)
    }

    if (match?.end) {
      // Highlight both brackets
      newDecorations.push(
        { from: match.start.from, to: match.start.to },
        { from: match.end.from, to: match.end.to },
      )
    }

    // Sort decorations by position
    newDecorations.sort((a, b) => a.from - b.from)

    return Decoration.set(
      newDecorations.map((pos) =>
        Decoration.mark({ class: MATCHING_BRACKET_CLASS }).range(pos.from, pos.to),
      ),
    )
  },

  provide: (field) => EditorView.decorations.from(field),
})

/**
 * DOM event handlers to detect mouse hover over brackets
 */
const bracketHoverPlugin = EditorView.domEventHandlers({
  mousemove(event, view) {
    const pos = view.posAtCoords({ x: event.clientX, y: event.clientY })

    if (pos !== null) {
      // Dispatch effect with position (findMatchingBracket will validate if it's a bracket)
      view.dispatch({
        effects: setHoverPos.of(pos),
      })
    }

    return false
  },

  mouseleave(_event, view) {
    // Clear decorations when mouse leaves editor
    view.dispatch({
      effects: setHoverPos.of(null),
    })
    return false
  },
})

export function bracketHoverMatching(): Extension {
  return [bracketHoverField, bracketHoverPlugin]
}
